import {makeAutoObservable, observable} from 'mobx'
import {Lang, Language} from 'yougile-lang'
import {
  HubProperties,
  UserUITheme,
  UserUIThemeVariation,
  UserUIThemeVariationPerTheme
} from 'yougile-model'
import {backgroundImages} from '@client/background-images'
import {Sync} from '@client/sync'
import {FileChooser} from '@common/file-chooser'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {showConfirm} from '@components/common/base/notifications'
import {LoggedInStore} from '@components/logged-in/logged-in-store'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {ThemeUtils} from '@extensions/theme-utils'

export interface Skins {
  custom: boolean
  path: string
}

interface State {
  currentSkin: string
  currentLanguage: Language
  currentTheme: UserUITheme
  themeVariation: UserUIThemeVariationPerTheme
  bgShowMore: boolean
  skins: Skins[]
  columnColorEnabled: boolean
}

export class SystemAppearanceStore {
  state: State = {
    skins: [],
    currentSkin: '',
    currentLanguage: Lang.getLang(),
    currentTheme: globals.theme,
    themeVariation: globals.themeVariation,
    bgShowMore: false,
    columnColorEnabled: false
  }

  constructor(private loggedInStore: LoggedInStore) {
    this.state.columnColorEnabled = globals.columnColorEnabled
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  calculateCustomSkins(): void {
    const user = Sync.getCurrentUser()
    const customSkins = [...user.customSkins].reverse().map(skin => ({
      custom: true,
      path: skin
    }))
    const defaultSkins = backgroundImages.map(skin => ({
      custom: false,
      path: skin
    }))
    this.state.skins = [...customSkins, ...defaultSkins]
    this.state.currentSkin = user.skin
  }

  loadBg = (event: React.MouseEvent): void => {
    const user = Sync.getCurrentUser()
    void FileChooser.uploadImage(event).then(({url}) => {
      user.customSkins.push(url)
      user.commit()
    })
  }

  setSkin = (skin: string): void => {
    const user = Sync.getCurrentUser()
    this.state.currentSkin = skin
    user.skin = skin || backgroundImages[0]
    user.commit()
  }

  deleteSkin = async (skin: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation()
    const user = Sync.getCurrentUser()
    if (!(await showConfirm(Lang.msg.confirmDeleteBg))) {
      return
    }
    if (user.skin === skin) {
      user.skin = backgroundImages[0]
      this.state.currentSkin = user.skin
    }
    const ix = user.customSkins.indexOf(skin)
    if (ix > -1) {
      user.customSkins.splice(ix, 1)
    }
    user.commit()
  }

  setBgShowMore(bgShowMore: boolean): void {
    this.state.bgShowMore = bgShowMore
  }

  getSkins(containerWidth: number): Skins[] {
    if (this.state.bgShowMore) {
      return this.state.skins
    } else {
      const skinWidth = 128
      const skinGapWidth = 12
      const width = skinWidth + skinGapWidth
      const maxWidth = 1248

      if (containerWidth >= maxWidth) {
        return this.state.skins.slice(0, 16)
      } else if (containerWidth >= maxWidth - width) {
        return this.state.skins.slice(0, 14)
      } else if (containerWidth >= maxWidth - width * 2) {
        return this.state.skins.slice(0, 12)
      } else if (containerWidth >= maxWidth - width * 3) {
        return this.state.skins.slice(0, 10)
      } else {
        return this.state.skins.slice(0, 8)
      }
    }
  }

  applyTheme(theme: 'dark' | 'light'): void {
    const user = Sync.getCurrentUser()
    user.theme = theme
    user.commit()
    this.state.currentTheme = user.theme
    const profile = Sync.getProfile()
    const p = profile.getHub(profile.companyId)
    if (p && !p.properties[HubProperties.triedDarkMode]) {
      p.properties[HubProperties.triedDarkMode] = 1
      p.commit()
      void Tracking.send(TrackingEvent.accountDarkModeTry)
    }
    void Tracking.send(TrackingEvent.accountDarkModeChange)
    void Tracking.sendUserProperty({theme})

    // Current variation depends on current theme
    const themeVariation = this.state.themeVariation[theme] ?? 'auto'
    ThemeUtils.trackThemeVariation(themeVariation)
  }

  changeLanguage(lang: Language): void {
    const user = Sync.getCurrentUser()
    user.language = lang
    user.commit()
    Lang.setLang(user.language)
    this.state.currentLanguage = Lang.getLang()
    void Tracking.sendUserProperty({lang})
  }

  toggleColumnColor(): void {
    const value = !globals.columnColorEnabled
    globals.setColumnColorEnabled(value)
    this.state.columnColorEnabled = value
  }

  setThemeVariation(themeVariation: UserUIThemeVariation): void {
    globals.setThemeVariation(this.state.currentTheme, themeVariation)
    this.state.themeVariation = {...globals.themeVariation}

    ThemeUtils.trackThemeVariation(themeVariation)
    void ThemeUtils.upTheme()
  }
}

const [SystemAppearanceContext, useSystemAppearanceStore] = createUseStore(
  SystemAppearanceStore
)
export {SystemAppearanceContext, useSystemAppearanceStore}
